import { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import ReactToPrint, { useReactToPrint } from "react-to-print";
// import PrintableInvoice from '../';

// Assets
import previousIcon from "../../../assets/previous_icon.png";
import nextIcon from "../../../assets/next_icon.png";
import printIcon from "../../../assets/printer_icon.png";
import archiveIcon from "../../../assets/archive_icon.png";
import editIcon from "../../../assets/edit_icon.png";
import saveIcon from "../../../assets/save_icon.png";
import closeIcon from "../../../assets/close_icon.png";

// Utils
import { DataContext } from "../../../utils/DataContext";
import useLanguage from "../../../utils/useLanguage";
import useRandomIcon from "../../../utils/useRandomIcon";

// Components
import AreYouSureModal from "../../../components/AreYouSureModal";
import Modal from "../../../components/Modal";
import AddNoCustomerContainerModal from "../AddNoCustomerContainerModal";
import ShippedContainerCard from "../../../components/ShippedContainerCard";
import BackToDashboard from "../../../components/BackToDashboard";
import Loader from "../../../components/Loader";
import SectionDivider from "../../../components/SectionDivider";
import CustomButton from "../../../components/CustomButton";
import classes from "../../../components/Modal.module.css";
import PDFUploader from "../../../components/PDFUploader";
import ErrorMessage from "../../../components/ErrorMessage";

// Page Sections
import NoCustomerInvoiceDetails from "./NoCustomerInvoiceDetails";
import EditNoCustomerInvoice from "./EditNoCustomerInvoice";
import TextDiv from "../../../components/TextDiv";
import PageWrapper from "../../../components/PageWrapper";
import InformationBox from "../../../components/InformationBox";
import NoCustomerInvoicePrintTemplate from "../../../components/NoCustomerInvoiceTemplate";
import PDFViewer from "../../../components/PDFViewer";

const ViewNoCustomerInvoice = ({}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("general");
  const { id } = useParams();
  const { currentDirection } = useLanguage();
  const pdfUploaderRef = useRef(null);
  const componentRef = useRef();

  const { error, setError } = useContext(DataContext);
  const getRandomIcon = useRandomIcon();

  const [showAddShippedContainerModal, setShowAddShippedContainerModal] =
    useState(false);
  const [editMode, setEditMode] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [showAreYouSure, setShowAreYouSure] = useState(false);

  // const [newRecord, setNewRecord] = useState({});
  const [invoiceDocs, setInvoiceDocs] = useState([]);
  const [tempDocs, setTempDocs] = useState([]);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputs, setInputs] = useState();
  const [invoice, setInvoice] = useState({
    invoiceNumber: "",
    amountForSupplier: "",
    abstractAmountForCustomer: "",
    finalAmountForCustomer: "",
    date: new Date(),
    received: "",
    left: "",
    notes: "",
    ShippedContainer: [],
  });
  const [paymentPercentage, setPaymentPercentage] = useState(null);

  // For Edit (Show are you sure modal)
  const [tempInvoice, setTempInvoice] = useState({
    invoiceNumber: "",
    amountForSupplier: "",
    abstractAmountForCustomer: "",
    finalAmountForCustomer: "",
    date: new Date(),
    received: "",
    left: "",
    notes: "",
    ShippedContainer: [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getMultiContainersInvoiceWithoutC/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response?.status === 200) {
        setLoading(false);
      }

      setInvoice(response?.data?.invoice);
      setPaymentPercentage(response.data.paymentPercentage);
      setTempInvoice({
        ...response?.data?.invoice,
        ShippedContainer: response?.data?.invoice?.ShippedContainer.map(
          (container) => {
            return {
              ...container,
              oldContainerNumber: container.containerNumber,
            };
          },
        ),
      });
      // setNewRecord(response?.data?.invoice);
      setInvoiceDocs(response?.data?.invoice?.documents);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handlePrintTemplate = useReactToPrint({
    contentRef: componentRef,
    pageStyle: `
    @page {
      margin: 0 !important;
      padding: 0 !important;
      size: A4;
    }
    @media print {
      @body {
          margin: 0 !important;
          padding: 0 !important;
          size: A4;
      } 
    }
  `,
  });

  const handleEdit = () => {
    setEditMode(true);
    setError("");
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setError("");
    setTempDocs([]);
    setShowAreYouSure(false);
  };

  const handleAttemptCancel = () => {
    const jsonInvoice = JSON.stringify(invoice);
    const jsonTempInvoice = JSON.stringify(tempInvoice);

    const jsonInvoiceDocs = JSON.stringify(invoice?.documents);
    const jsonInvoiceTempDocs = JSON.stringify([...invoiceDocs, ...tempDocs]);

    if (
      jsonInvoice === jsonTempInvoice &&
      jsonInvoiceDocs === jsonInvoiceTempDocs
    ) {
      handleCancelEdit();
    } else {
      setShowAreYouSure(true);
    }
  };

  const handleChildUploadDocs = async (multiContainersInvoiceId) => {
    if (pdfUploaderRef.current) {
      await pdfUploaderRef.current.handleUploadDocs(multiContainersInvoiceId);
    }
  };

  const handleConfirmEdit = async (invoice) => {
    const token = localStorage.getItem("token");
    const userId = parseInt(localStorage.getItem("userId"), 10);
    const organizationId = parseInt(localStorage.getItem("organizationId"), 10);
    const newInvoice = invoice ?? tempInvoice;

    try {
      setLoadingModal(true);
      // Send a PUT request to the backend to update the invoice with the existing data
      const editedInvoice = await axios.put(
        `${process.env.REACT_APP_URL}/updateInvoiceOfMultiContsWithoutC/${
          invoice?.id || id
        }`,
        {
          ...newInvoice,
          organizationId,
        },
        // { ...invoice, ...inputs }, // Send the updated record data
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": userId,
            "Supplier-Id": invoice?.supplierId,
            // "Organization-Id": organizationId,
          },
        },
      );

      const { status, error } = editedInvoice;
      // Log the edited invoice data returned by the backend

      // Update the invoice data with the returned changes from the backend
      if (editedInvoice?.status === 200) {
        setInvoice((prev) => {
          return { ...prev, ...editedInvoice?.data?.invoice };
        });
        setError("");
      }

      await handleChildUploadDocs(editedInvoice?.data?.invoice?.id);
      setEditMode(false);
    } catch (error) {
      // setError(errorMsg || "Error updating invoice.");
      const errMsg = error.response?.data?.message;
      if (errMsg === "alreadyLinkedToAnInvoice") {
        const containerNumber = error?.response?.data?.containerNumber;
        setError(`${t("container")} ${containerNumber} ${t(errMsg)}`);
      } else {
        setError(errMsg || "An error occurred");
      }

      // Log and display any errors that occur during the update process
      console.error("Error updating invoice:", error);
    } finally {
      window.scrollTo(0, 0);
      setLoadingModal(false);
    }
  };

  const handleArchive = async () => {
    const token = localStorage.getItem("token");
    const userId = parseInt(localStorage.getItem("userId"), 10);

    try {
      setLoadingModal(true);
      const archivedInvoice = await axios.delete(
        `${process.env.REACT_APP_URL}/archiveInvoiceOfMultiContainers/${invoice?.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!archivedInvoice?.data) {
        console.error(`archivedInvoice.error`, archivedInvoice.error);
      }

      navigate(`/noCustomerInvoices`);
    } catch (error) {
      console.log("THIS ERROR", error);
      console.error("Error updating invoice:", error);
      setError(error?.response?.data?.message);
    } finally {
      setLoadingModal(false);
    }
  };

  const handleCloseArchiveModal = () => setShowArchiveModal(false);

  return (
    <>
      <PageWrapper className="add-shipped-container-page">
        {!editMode ? (
          <div className="add-shipped-container-top-actions">
            <div className="user-logout">
              <BackToDashboard
                title={t("back")}
                variant="blank"
                icon={currentDirection === "ltr" ? previousIcon : nextIcon}
                iconSize={14}
              />
            </div>
          </div>
        ) : null}
        {loading ? (
          <Loader />
        ) : (
          <>
            <h3 className={"add-shipped-container-title"}>
              {t("invoiceDetails")}
            </h3>
            {/* {error && !loadingModal && <ErrorBox>{error}</ErrorBox>} */}
            <div className={` add-shipped-container-invoice-part`}>
              <TextDiv
                title="supplier"
                value={invoice?.supplier?.name}
                divClasses="item-invoice-div"
                spanClasses="u-flex_flex-2 t-bold_bold"
                pClasses="t-bold_normal u-width-specific_150"
              />

              <TextDiv
                title="company"
                value={invoice?.subCompany?.nameInAr}
                divClasses="item-invoice-div"
                spanClasses="u-flex_flex-2 t-bold_bold"
                pClasses="t-bold_normal u-width-specific_150"
              />

              <TextDiv
                title="createdBy"
                value={invoice?.user?.username}
                divClasses="item-invoice-div"
                spanClasses="u-flex_flex-2 t-bold_bold"
                pClasses="t-bold_normal u-width-specific_150"
              />

              {editMode ? (
                <EditNoCustomerInvoice
                  invoice={tempInvoice}
                  setInvoice={setTempInvoice}
                />
              ) : (
                <>
                  <NoCustomerInvoiceDetails invoice={invoice} />

                  <SectionDivider title={"bankApplicationInfo"} />

                  <section>
                    {invoice?.bank ? (
                      <div>
                        <TextDiv
                          title="bankAppCreator"
                          value={invoice?.bank ? invoice?.bankAppCreator : ""}
                          divClasses="item-invoice-div"
                          spanClasses="u-flex_flex-2 t-bold_bold"
                          pClasses="t-bold_normal u-width-specific_150"
                        />
                        <TextDiv
                          title="bankName"
                          value={invoice?.bank?.name}
                          divClasses="item-invoice-div"
                          spanClasses="u-flex_flex-2 t-bold_bold"
                          pClasses="t-bold_normal u-width-specific_150"
                        />

                        <TextDiv
                          title="paymentPercentage"
                          value={`${
                            Number(paymentPercentage.toFixed(2)) || 0
                          }%`}
                          divClasses="item-invoice-div"
                          spanClasses="u-flex_flex-2 t-bold_bold"
                          pClasses="t-bold_normal u-width-specific_150"
                        />
                      </div>
                    ) : (
                      <InformationBox>
                        <p>{t("noBankApps")}</p>
                      </InformationBox>
                    )}
                  </section>

                  {/* Containers */}

                  {invoice?.ShippedContainer?.length ? (
                    <SectionDivider title="containers" />
                  ) : null}
                  <div className="all-containers u-styled-scrollbar u-margin--bottom_700">
                    {invoice?.ShippedContainer?.map((item, i) => (
                      <ShippedContainerCard
                        key={i}
                        index={i}
                        src={getRandomIcon()}
                        containersLength={invoice?.ShippedContainer?.length}
                        currentDirection={currentDirection}
                        containerInfo={item}
                        subCompanyInfo={invoice?.subCompany}
                        withCustomer={false}
                        invoice={tempInvoice}
                        setInvoice={setTempInvoice}
                        handleConfirmEdit={handleConfirmEdit}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
            <>
              {invoice?.documents?.length ? (
                <>
                  <SectionDivider title="invoiceAllDocs" />
                  {invoice?.documents.map((doc) => (
                    <div>
                      <p>{t(doc.type)}</p>
                      <PDFViewer
                        fileId={doc.S3Key}
                        filePath={doc.filePath}
                        mode="show"
                        fromFile={true}
                      />
                    </div>
                  ))}
                </>
              ) : null}

              <PDFUploader
                ref={pdfUploaderRef}
                invoiceNumber={invoice?.invoiceNumber}
                invoiceDocs={invoiceDocs}
                setInvoiceDocs={setInvoiceDocs}
                mode={!editMode && "show"}
                documents={invoiceDocs}
                tempDocs={tempDocs}
                setTempDocs={setTempDocs}
                invoiceType="multiContainersInvoice"
                title="noTitle"
              />
            </>
            <div className="o-column o-column--vertical-align_end">
              <ErrorMessage>{t(error)}</ErrorMessage>
              <section className={classes.actions}>
                {editMode ? (
                  <div className="o-column">
                    {loadingModal ? <Loader /> : null}
                    <div className={classes.actions}>
                      <CustomButton
                        title={t("confirm")}
                        onClick={() => handleConfirmEdit()}
                        icon={saveIcon}
                        iconSize={16}
                        disabled={loadingModal}
                      />
                      <CustomButton
                        title={t("cancel")}
                        onClick={handleAttemptCancel}
                        variant="blank"
                        icon={closeIcon}
                        iconSize={14}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <CustomButton
                      variant="blank"
                      title={t("edit")}
                      onClick={handleEdit}
                      icon={editIcon}
                      iconSize={18}
                    />
                    {setEditMode ? (
                      <>
                        <CustomButton
                          title={t("print")}
                          onClick={handlePrintTemplate}
                          variant="gray"
                          icon={printIcon}
                          iconSize={18}
                        />

                        {!invoice?.archived && (
                          <CustomButton
                            title={t("archive")}
                            variant="red"
                            icon={archiveIcon}
                            iconSize={20}
                            onClick={() => setShowArchiveModal(true)}
                          />
                        )}
                      </>
                    ) : null}
                  </>
                )}
              </section>
            </div>
          </>
        )}
      </PageWrapper>
      {/* Render Modal */}

      <div style={{ display: "none" }}>
        <NoCustomerInvoicePrintTemplate ref={componentRef} invoice={invoice} />
      </div>

      {showAddShippedContainerModal && (
        <AddNoCustomerContainerModal
          inputs={inputs}
          setInputs={setInputs}
          onClose={() => setShowAddShippedContainerModal(false)}
          setShowAddShippedContainerModal={() =>
            setShowAddShippedContainerModal(false)
          }
        />
      )}

      {showArchiveModal && (
        <AreYouSureModal
          onClose={handleCloseArchiveModal}
          onConfirm={handleArchive}
          confirmIconType="save"
          title={t("archiveInvoice")}
          customMessage={`${t("archiveInvoice")} ${invoice?.invoiceNumber}`}
          confirmTitle={t("confirm")}
          closeTitle={t("cancel")}
          loading={loadingModal}
          error={error ? t(error) : undefined}
          modalSize="medium"
          confirmButtonVariant="confirm"
        />
      )}

      {showAreYouSure ? (
        <AreYouSureModal
          onClose={() => setShowAreYouSure(false)}
          onConfirm={handleCancelEdit}
          confirmIconType="whiteCheck"
        />
      ) : null}
    </>
  );
};
// handleClose
export default ViewNoCustomerInvoice;
